/**
 * Home Dashboard "Tasks" card: a READ-ONLY projection of the existing Project
 * Tasks. No new table, cache or task system.
 *
 *  - Source: public.project_tasks through the same organization / project
 *    visibility the rest of the app uses (projects via loadScopedActiveProjects).
 *  - Employee: the task query itself is narrowed to assignee_id = me, so other
 *    people's and unassigned tasks are never fetched; the model re-checks it.
 *  - Owner / admin / manager: organization-wide, unassigned included.
 *  - Guest: nothing.
 *  - Open, dated tasks only. Overdue = due < today; Today = due == today;
 *    Coming up = tomorrow .. today + 7.
 *  - Offline: the device copies Planning already reads (field project snapshot
 *    + cached Project Tasks); the same role filter is applied to them.
 */
import { supabase } from "../../supabase";
import { CAPABILITY, can, isEmployee } from "../permissions";
import { isOffline } from "../connectivity";
import { shouldSkipSupabase } from "../guestMode";
import { resolveOrganizationIdForQueries } from "../orgQueryScope";
import { loadScopedActiveProjects } from "../operationalQueryAdapters";
import { readCachedFieldLocations, readCachedFieldProjects } from "../projectFieldSnapshot";
import { readCachedProjectTasks } from "../tasks/tasksCache";
import { isTasksUnavailableError } from "../tasks/projectTasks";
import { dayIndex, dayIndexToISO, isPlannableProject } from "../planning/planningModel";

export const DASHBOARD_TASK_HORIZON_DAYS = 7;
const PROJECT_SELECT =
  "project_id, project_name, project_number, status, is_active, client_id, organization_id";
const CHUNK = 60;

const chunk = (ids) => {
  const out = [];
  for (let i = 0; i < ids.length; i += CHUNK) out.push(ids.slice(i, i + CHUNK));
  return out;
};

/** Guests never get the operational Tasks card. */
export function canSeeDashboardTasks(actor) {
  return Boolean(actor) && !actor.isGuest && can(actor, CAPABILITY.PROJECT_TASK_VIEW);
}

/** Pure projection. Inputs are raw rows; output is grouped, ordered, display-ready. */
export function buildDashboardTasks({
  tasks = [],
  projects = [],
  locations = [],
  features = [],
  viewer,
  today,
  horizonDays = DASHBOARD_TASK_HORIZON_DAYS,
}) {
  const todayIdx = dayIndex(today);
  const empty = { overdue: [], today: [], coming: [], total: 0 };
  if (todayIdx == null || !viewer || viewer.isGuest) return empty;

  const employee = isEmployee(viewer);
  const me = viewer.supabase_user_id || viewer.id || null;
  const projectById = new Map(projects.filter(isPlannableProject).map((p) => [p.project_id, p]));
  const locationById = new Map(locations.map((l) => [l.project_location_id, l]));
  const featureById = new Map(features.map((f) => [f.project_feature_id, f]));
  const horizon = todayIdx + horizonDays;

  const seen = new Set();
  const rows = [];
  tasks.forEach((task) => {
    if (!task || seen.has(task.project_task_id)) return;
    if (task.is_active === false || task.status === "completed") return;
    const project = projectById.get(task.project_id);
    if (!project) return;
    // Employee = MY tasks: assigned to me only (never unassigned, never others').
    if (employee && (!me || task.assignee_id !== me)) return;
    const due = dayIndex(task.due_date);
    if (due == null || due > horizon) return;
    seen.add(task.project_task_id);

    const feature = task.feature_id ? featureById.get(task.feature_id) : null;
    const location = locationById.get(task.location_id || feature?.project_location_id);
    rows.push({
      id: task.project_task_id,
      title: task.title || "Task",
      dueIndex: due,
      due: task.due_date,
      projectName: project.project_name || "Project",
      path: [project.project_name || "Project", location?.name, feature?.name].filter(Boolean).join(" › "),
      assigneeId: task.assignee_id || null,
      sort: Number.isFinite(Number(task.sort_order)) ? Number(task.sort_order) : 0,
      created: String(task.created_at || ""),
    });
  });

  rows.sort(
    (a, b) =>
      a.dueIndex - b.dueIndex ||
      a.sort - b.sort ||
      a.created.localeCompare(b.created) ||
      String(a.id).localeCompare(String(b.id)),
  );

  const out = { overdue: [], today: [], coming: [], total: rows.length };
  rows.forEach((row) => {
    if (row.dueIndex < todayIdx) out.overdue.push({ ...row, overdue: true });
    else if (row.dueIndex === todayIdx) out.today.push(row);
    else out.coming.push(row);
  });
  return out;
}

/** Rows to show per section: tighter when all three sections are populated. */
export function visibleLimit(groups) {
  const populated = ["overdue", "today", "coming"].filter((k) => groups[k].length > 0).length;
  return populated >= 3 ? 2 : 3;
}

function fromDevice(actor) {
  const projects = readCachedFieldProjects(actor);
  const locations = [];
  const features = [];
  const tasks = [];
  projects.forEach((project) => {
    (readCachedFieldLocations(actor, project.project_id) || []).forEach((location) => {
      locations.push(location);
      (location.features || []).forEach((feature) =>
        features.push({ ...feature, project_location_id: location.project_location_id }),
      );
    });
    tasks.push(...(readCachedProjectTasks(actor, project.project_id) || []));
  });
  return { projects, locations, features, tasks, fromDevice: true };
}

async function selectIn(table, organizationId, column, ids, apply) {
  const rows = [];
  for (const part of chunk(ids)) {
    let query = supabase.from(table).select("*").eq("organization_id", organizationId);
    if (apply) query = apply(query);
    const { data, error } = await query.in(column, part);
    if (error) throw error;
    rows.push(...(data || []));
  }
  return rows;
}

/** Load the raw rows for the card. Returns null when the card must not be shown. */
export async function loadDashboardTaskData(actor, { today }) {
  if (!canSeeDashboardTasks(actor)) return null;
  if (shouldSkipSupabase(actor)) return null;
  if (isOffline()) return fromDevice(actor);

  try {
    const projects = await loadScopedActiveProjects(supabase, actor, {}, PROJECT_SELECT, { activeOnly: true });
    const ids = projects.map((p) => p.project_id);
    if (ids.length === 0) return { projects: [], locations: [], features: [], tasks: [], fromDevice: false };
    const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
    if (!organizationId) throw new Error("Organization is required.");

    const horizon = dayIndexToISO(dayIndex(today) + DASHBOARD_TASK_HORIZON_DAYS);
    const me = actor.supabase_user_id || actor.id;
    let tasks;
    try {
      tasks = await selectIn("project_tasks", organizationId, "project_id", ids, (query) => {
        let q = query.eq("is_active", true).eq("status", "open").lte("due_date", horizon);
        // Employees: enforce "my tasks" in the query, before anything reaches the client.
        if (isEmployee(actor)) q = q.eq("assignee_id", me);
        return q;
      });
    } catch (error) {
      if (isTasksUnavailableError(error)) return null;
      throw error;
    }

    const locationIds = [...new Set(tasks.map((t) => t.location_id).filter(Boolean))];
    const featureIds = [...new Set(tasks.map((t) => t.feature_id).filter(Boolean))];
    const features = featureIds.length
      ? await selectIn("project_features", organizationId, "project_feature_id", featureIds)
      : [];
    const allLocationIds = [...new Set([...locationIds, ...features.map((f) => f.project_location_id)])];
    const locations = allLocationIds.length
      ? await selectIn("project_locations", organizationId, "project_location_id", allLocationIds)
      : [];
    return { projects, locations, features, tasks, fromDevice: false };
  } catch (error) {
    const device = fromDevice(actor);
    if (device.projects.length) return device;
    return null;
  }
}
